import type { Language } from '@inflearn/i18n-tools';
import { isEmpty, isNil } from 'lodash-es';
import { i18n } from 'next-i18next';

import {
  NEXT_PUBLIC_MENTORING_SITE_URL,
  NEXT_PUBLIC_RALLIT_SITE_URL,
  NEXT_PUBLIC_SITE_URL,
} from '../../appConfig';

export const DEFAULT_LANG = 'ko';

export function generateSiteURL(
  path: string,
  options: Omit<TranslatedSiteUrlOptions, 'path'> & { useLang?: boolean } = { useLang: false },
) {
  return generateTranslatedSiteUrl({
    ...options,
    path,
  });
}

// ================================
// !!! NOTE !!!
// 모든 URL의 value는 함수 타입으로 관리하여
// i18n 초기화 이후 평가될 수 있도록 한다.
// ================================
interface TranslatedSiteUrlOptions {
  baseURL?: string;
  path: string;
  specificLang?: Language;
}

export function generateTranslatedSiteUrl({
  path,
  specificLang,
  baseURL = NEXT_PUBLIC_SITE_URL,
}: TranslatedSiteUrlOptions): string {
  if (baseURL !== NEXT_PUBLIC_SITE_URL || baseURL !== NEXT_PUBLIC_MENTORING_SITE_URL) {
    return (baseURL ?? '') + path;
  }

  const locale = (specificLang || i18n?.language) ?? DEFAULT_LANG;
  const isKo = locale === DEFAULT_LANG;

  const localePath = isKo ? '' : `/${locale}`;

  return `${baseURL}${localePath}${path}`;
}

export function generateRallitSiteURL(path: string) {
  return `${NEXT_PUBLIC_RALLIT_SITE_URL}${path}`;
}

const INFLEARN_DOMAINS: Readonly<string[]> = ['devinflearn.com', 'inflearn.com'] as const;

const RALLIT_DOMAINS: Readonly<string[]> = ['devrallit.com', 'rallit.com'] as const;

const OUR_DOMAINS = [...INFLEARN_DOMAINS, ...RALLIT_DOMAINS];

/** @see {@link https://github.com/inflearn/ant-man-account/blob/develop/lib/isWhiteRedirectUrl.js#L12} */
export const isOurUrl = (url?: string | null): url is string => {
  if (isNil(url) || isEmpty(url)) {
    return false;
  }

  if (url.startsWith('/')) {
    return true;
  }

  try {
    const { hostname } = new URL(decodeURIComponent(url));

    if (hostname.includes('localhost')) {
      return true;
    }

    return OUR_DOMAINS.includes(getSecondLevelDomain({ hostname }));
  } catch {
    return false;
  }
};

export const isRallitUrl = (url?: string | null): url is string => {
  if (isNil(url) || isEmpty(url)) {
    return false;
  }

  if (url.startsWith('/')) {
    return true;
  }

  try {
    const { hostname } = new URL(url);

    if (hostname.includes('localhost')) {
      return true;
    }

    return RALLIT_DOMAINS.includes(getSecondLevelDomain({ hostname }));
  } catch {
    return false;
  }
};

export const getSecondLevelDomain = ({ hostname }: Pick<URL, 'hostname'>) =>
  hostname.split('.').length === 3 ? hostname.slice(hostname.indexOf('.') + 1) : hostname;
